import { Box, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import React from 'react';
import { useIntl } from 'react-intl';

import { useChartPalette } from 'components/charts/hooks/use-chart-palette';
import { BenchmarkSentiment } from 'views/benchmark-analytics/types/benchmark-sentiment.type';
import { deltaLabelId, sentimentColor } from 'views/benchmark-analytics/utils/benchmark-math';

type Props = {
  text: string;
  delta: number | null;
  sentiment: BenchmarkSentiment;
};

const MARKER_SIZE = 14;
const DOT_SIZE = 8;

const DotSlot = styled('span', { shouldForwardProp: (prop) => prop !== 'dotColor' })<{ dotColor: string }>(({ dotColor }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
  width: MARKER_SIZE,
  height: MARKER_SIZE,
  '&::after': {
    content: '""',
    width: DOT_SIZE,
    height: DOT_SIZE,
    borderRadius: '50%',
    backgroundColor: dotColor,
  },
}));

// Neutral dot for the Δ column header, sized like the series markers in the other headers.
export const DeltaHeaderDot: React.FC = () => {
  const palette = useChartPalette();
  return <DotSlot dotColor={palette.text.secondary} aria-hidden />;
};

// The label comes from the sign of Δ; the color comes from sentiment.
const BenchmarkDeltaCell: React.FC<Props> = ({ text, delta, sentiment }) => {
  const intl = useIntl();
  const palette = useChartPalette();
  const color = sentimentColor(palette, sentiment);
  const labelId = deltaLabelId(delta, sentiment);

  return (
    <Box
      display="flex"
      alignItems="center"
      justifyContent="flex-end"
      width="100%"
      height="100%"
      role={labelId ? 'img' : undefined}
      aria-label={labelId ? `${text}, ${intl.formatMessage({ id: labelId })}` : undefined}
    >
      <Typography variant="body2" fontSize={14} fontWeight={600} color={color}>
        {text}
      </Typography>
    </Box>
  );
};

export default BenchmarkDeltaCell;
